import React from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { LaunchVideoDialog } from "@/components/LaunchVideoDialog";
import { Button } from "@/components/ui/button";
import { Store, Gift, BadgeCheck, Sparkles, Bell, MapPin, Users, ArrowRight, CheckCircle2, PlayCircle } from "lucide-react";

const BENEFITS = [
  { icon: Users, title: "Richieste reali dalla tua zona", text: "Le persone della tua zona pubblicano ciò di cui hanno bisogno. Tu rispondi a chi cerca proprio il tuo servizio." },
  { icon: Gift, title: "Contatto diretto", text: "Parli direttamente con chi ha bisogno del tuo servizio, senza intermediari." },
  { icon: BadgeCheck, title: "Profilo verificato", text: "Ottieni il badge \u201cAttività verificata\u201d: chi cerca si fida di più e ti contatta con più facilità." },
  { icon: Bell, title: "Nessuna occasione persa", text: "Ricevi le nuove richieste della tua categoria e rispondi prima degli altri." },
];

const STEPS = [
  { n: "1", title: "Registra la tua attività", text: "Bastano un minuto e i dati della tua attività." },
  { n: "2", title: "Ricevi le richieste", text: "Le persone della zona pubblicano i loro bisogni: tu rispondi con un messaggio o un preventivo." },
  { n: "3", title: "Conquista nuovi clienti", text: "Più rispondi bene, più recensioni ottieni e più sei visibile su FYDATE." },
];

export default function BusinessLanding() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [videoOpen, setVideoOpen] = React.useState(false);

  return (
    <Layout>
      <LaunchVideoDialog open={videoOpen} onOpenChange={setVideoOpen} />
      <div className="max-w-3xl mx-auto fade-in" data-testid="business-landing">
        {/* Hero */}
        <section className="text-center pt-2 pb-8">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#FFF1E6] border border-[#FFD8BF] text-[#B8420A] text-xs font-semibold px-3 py-1 mb-4">
            <Store size={14} /> Per artigiani e attività
          </span>
          <h1 className="font-serif text-4xl sm:text-5xl font-bold text-[#0F1230] leading-tight">
            Fatti trovare da chi cerca te,<br className="hidden sm:block" /> nella tua zona
          </h1>
          <p className="text-[#2A2E48] text-base sm:text-lg mt-4 max-w-xl mx-auto">
            FYDATE è la bacheca dei bisogni della tua zona. Le persone pubblicano ciò che cercano,
            tu rispondi e trovi nuovi clienti. <b className="text-[#141A3A]">Senza intermediari.</b>
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-7">
            <Button data-testid="landing-register-cta" onClick={() => navigate(user ? "/profile" : "/register")}
              className="h-12 px-7 rounded-full bg-[#FFD23F] hover:bg-[#F2C200] text-[#0F1230] text-base w-full sm:w-auto">
              {user ? "Completa la tua scheda" : "Registra la tua attività"} <ArrowRight size={18} className="ml-1" />
            </Button>
            <Button data-testid="landing-promote-cta" onClick={() => navigate("/promuovi")} variant="outline"
              className="h-12 px-7 rounded-full border-[#E8590C] text-[#B8420A] hover:bg-[#FFF1E6] text-base w-full sm:w-auto">
              <Sparkles size={16} className="mr-1" /> Mettiti in evidenza
            </Button>
          </div>
          <p className="text-xs text-[#4A4E68] mt-3 inline-flex items-center gap-1">
            <CheckCircle2 size={13} className="text-[#2E7D32]" /> Nessun costo per iniziare · Nessuna carta richiesta
          </p>
          <div className="mt-4">
            <button data-testid="landing-video-btn" onClick={() => setVideoOpen(true)} className="inline-flex items-center gap-1.5 text-sm font-medium text-[#141A3A] hover:underline underline-offset-2">
              <PlayCircle size={18} /> Guarda il video di lancio
            </button>
          </div>
        </section>

        {/* Benefits */}
        <section className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-10">
          {BENEFITS.map((b, i) => {
            const Icon = b.icon;
            return (
              <div key={i} data-testid={`landing-benefit-${i}`} className="bg-white rounded-[18px] border border-[#DADFEA] p-5">
                <div className="w-11 h-11 rounded-xl bg-[#FFF6DA] border border-[#F6DC7A] flex items-center justify-center mb-3">
                  <Icon size={20} className="text-[#141A3A]" />
                </div>
                <h3 className="font-semibold text-[#0F1230] mb-1">{b.title}</h3>
                <p className="text-sm text-[#4A4E68] leading-relaxed">{b.text}</p>
              </div>
            );
          })}
        </section>

        {/* How it works */}
        <section className="mb-10">
          <h2 className="font-serif text-2xl font-bold text-[#0F1230] text-center mb-6">Come funziona</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {STEPS.map((s) => (
              <div key={s.n} data-testid={`landing-step-${s.n}`} className="text-center">
                <div className="w-12 h-12 rounded-full bg-[#1F4FE0] text-white font-serif text-xl font-bold flex items-center justify-center mx-auto mb-3">{s.n}</div>
                <h3 className="font-semibold text-[#0F1230] mb-1">{s.title}</h3>
                <p className="text-sm text-[#4A4E68] leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Trust strip */}
        <section className="bg-[#FFF6DA] border border-[#F6DC7A] rounded-[18px] p-6 mb-10 flex items-start gap-4">
          <BadgeCheck size={28} className="text-[#2E7D32] shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-[#0F1230]">Le attività verificate si fanno notare</h3>
            <p className="text-sm text-[#2A2E48] mt-1 leading-relaxed">
              Dopo la registrazione possiamo verificare la tua attività e assegnarti il badge
              <span className="inline-flex items-center gap-1 rounded-full bg-[#E8F5E9] text-[#2E7D32] px-2 py-0.5 text-xs font-medium mx-1 align-middle"><BadgeCheck size={12} /> Attività verificata</span>
              mostrato accanto al tuo nome. Più fiducia, più contatti.
            </p>
          </div>
        </section>

        {/* Final CTA */}
        <section className="text-center bg-white border border-[#DADFEA] rounded-[18px] p-8 mb-6">
          <MapPin size={26} className="text-[#E8590C] mx-auto mb-2" />
          <h2 className="font-serif text-2xl font-bold text-[#0F1230]">Inizia oggi</h2>
          <p className="text-[#4A4E68] mt-2 mb-5 max-w-md mx-auto">Unisciti alle attività della tua zona già presenti su FYDATE.</p>
          <Button data-testid="landing-register-cta-bottom" onClick={() => navigate(user ? "/profile" : "/register")}
            className="h-12 px-8 rounded-full bg-[#FFD23F] hover:bg-[#F2C200] text-[#0F1230] text-base">
            {user ? "Completa la tua scheda" : "Registra la tua attività"} <ArrowRight size={18} className="ml-1" />
          </Button>
          <div className="mt-4">
            <button data-testid="landing-locandina-link" onClick={() => navigate("/locandina")} className="text-sm text-[#141A3A] underline underline-offset-2">
              Scarica la locandina con QR da stampare
            </button>
          </div>
        </section>
      </div>
    </Layout>
  );
}
